import { XAxis, ResponsiveContainer, AreaChart, Area, YAxis, CartesianGrid, Tooltip, TooltipProps } from "recharts";
import React, { useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import { useSelectedCareRecipient } from "../../hooks/care_recipient/useSelectedCareRecipient";
import { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";
import { LeftRectangleIcon } from "../common/icons/LeftRectangleIcon";
import { RightRectangleIcon } from "../common/icons/RightRectangleIcon";
import { ApiRequest } from "../../../state/api/ApiRequest";
import { TemperatureHumidityGraph } from "./components/TemperatureHumidityGraph";

// 岩谷から取得した電力データのグラフを描画するコンポーネント
export function ElectricGraph() {

    const [date, setDate] = useState(dayjs());
    const [data, setData] = useState<ElectricPowerUsage[]>([]);
    const careRecipient = useSelectedCareRecipient();

    const onClickNextDay = () => {
        setDate(prev => prev.add(1, "day"));
    };

    const onClickPreviousDay = () => {
        setDate(prev => prev.add(-1, "day"));
    };

    const disabled = useMemo(() => {
        const today = dayjs().startOf("day");
        const isTodayOrFuture = date.isSame(today, "day") || date.isAfter(today, "day");
        return isTodayOrFuture;

    }, [date]);

    useEffect(() => {
        if (!careRecipient) {
            return;
        }
        const dateFrom = date.startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const dateTo = date.endOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const getData = async () => {
            const data = await ApiRequest.get(
                `/api/electric/${careRecipient?.customerCode?.value}`,
                {
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                },
                () => {},
                () => {},
            );
            if ("error" in data) {
                setData([]);
            } else {
                setData(data);
            }
        };
        getData();
    }, [careRecipient, date]);

    const formatXAxis = (value: any, _index: number) => {
        const day = dayjs(value);
        return day.format("HH:mm");
    };

    const maxYAxis = useMemo(() => {
        const max = Math.max(...data.map(d => d.power));
        return max + 200; //少し余裕を持たせる
    }, [data]);

    const CustomTooltip = ({
        active,
        payload,
        label,
    }: TooltipProps<ValueType, NameType>) => {
        if (!payload || payload.length === 0) return null;
        if (active) {
            const day = dayjs(label).format("MM月DD日");
            const time = dayjs(label).format("HH:mm");
            return (
                <div className="custom-tooltip">
                    <div>
                        <span className={"day"}>{day}</span>
                        <span className={"time"}>{time}</span>
                    </div>
                    <div className={"wat_value"}>{payload[0].value} Wh</div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className={"dash_board"}>
            <div className={"date_selector"}>
                <button onClick={onClickPreviousDay}>
                    <LeftRectangleIcon />
                </button>
                <div className={"date"}>
                    <div className={"year"}>
                        {date.format("YYYY年")}
                    </div>
                    <div className={"month_day"}>
                        <span>{date.format("MM")}</span>
                        <span className={"small"}>月</span>
                        <span>{date.format("DD")}</span>
                        <span className={"small"}>日</span>
                    </div>
                </div>
                <button onClick={onClickNextDay} disabled={disabled} className={"date_selector__button"}>
                    <RightRectangleIcon />
                </button>
            </div>
            <div className={classNames("graph", "large")}>
                <h2 className={"title"}>{"電力"}</h2>
                <div className={"graph_area"}>
                    <ResponsiveContainer height={"90%"} >
                        <AreaChart data={data} >
                            <defs>
                                <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                                </linearGradient>
                                <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <Tooltip content={<CustomTooltip />}/>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="timestamp" axisLine={true} tickFormatter={formatXAxis} />
                            <YAxis dataKey="power" axisLine={true} domain={[0, maxYAxis]}/>
                            <Area type="monotone" dataKey="power" stroke="#82ca9d" fill={"url(#colorPv)"} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <TemperatureHumidityGraph
                customerCode={careRecipient?.customerCode ?? null}
                targetDate={date}
            />
        </div>
    );
}

type ElectricPowerUsage = {
    // 時刻は30分ごとのデータとなる予定
    timestamp: string,
    // 使用したワット数
    power: number,
}
